import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import {
    AttachMoney as PricingIcon,
    Campaign as EmailIcon,
    CheckCircle as StatusIcon,
    Home as HomeIcon,
    LocalOffer as DiscountIcon,
    PhoneIphone as MobilityIcon,
    Security as SecurityIcon,
    Settings as AddonIcon,
    History as HistoryIcon,
    Tv as TvIcon,
    Visibility as ViewIcon,
    Wifi as InternetIcon
} from '@mui/icons-material';
import {
    normalizeMobilityLinePricingForEditor,
    serializeMobilityLinePricing
} from './fields/mobilityLinePricing';
import {
    savePricingDraft
} from './api/pricingConfigApi';
import {
    pricingScopeLabel
} from './hooks/useDraftWorkflow';
import { useProductEditorState } from './hooks/useProductEditorState';
import ProductPricingActiveTab from './components/ProductPricingActiveTab';
import ProductPricingEditorDialogs from './editors/ProductPricingEditorDialogs';
import {
    serializeInternetEditor,
    validateInternetEditor
} from './editors/InternetEditor';
import {
    serializeHomePhoneEditor,
    validateHomePhoneEditor
} from './editors/HomePhoneEditor';
import {
    serializeMobilityEditor,
    validateMobilityEditor
} from './editors/MobilityEditor';
import {
    serializeSecurityEditor,
    validateSecurityEditor
} from './editors/SecurityEditor';
import {
    serializeAddonsEditor,
    validateAddonsEditor
} from './editors/AddonsEditor';
import {
    serializeTvDisplayEditor,
    tvPvrModes,
    validateTvDisplayEditor
} from './editors/TvDisplayEditor';
import {
    serializeEmailShellEditor,
    validateEmailShellEditor
} from './editors/EmailShellEditor';
import {
    getChipColorIssue,
    isValidSortOrderInput,
    normalizeChipType,
    normalizeDisplayChipEntry,
    normalizeDisplayChipList,
    safeChipColor,
    safeSortOrder,
    sortedDisplayChips
} from './fields/displayMetadata';
import {
    getLifecycleStatus,
    normalizeDateTimeInput
} from './fields/lifecycleMetadata';

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return String(value);
    }

    return Number.isInteger(numericValue) ? `$${numericValue}` : `$${numericValue.toFixed(2)}`;
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const shortText = (value, maxLength = 96) => {
    const text = String(value || '').trim();
    if (text.length <= maxLength) {
        return text;
    }

    return `${text.slice(0, maxLength - 1)}...`;
};

const formatDateTime = (value) => {
    if (!value) {
        return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
};

const editableHomePhoneFields = [
    'displayName',
    'price',
    'regularPrice',
    'requiredFees',
    'status',
    'visibleInAdminBuilder',
    'visibleInAgentBuilder',
    'startsAt',
    'expiresAt',
    'autoHideAfterExpiry',
    'archivedAt',
    'internalNotes',
    'emailDisplayName',
    'emailBullets',
    'visible',
    'quoteEligible',
    'customerEmailEligible',
    'sortOrder',
    'display'
];

const editableInternetFields = [
    'displayName',
    'belowLinePrice',
    'aboveLinePrice',
    'mobilityBelowLinePrice',
    'mobilityAboveLinePrice',
    'eppMobilityBelowLinePrice',
    'eppMobilityAboveLinePrice',
    'pabAdjustmentAmount',
    'emailRegularPrice',
    'mobilityDiscountEligible',
    'eppEligible',
    'badges',
    'badgeColor',
    'recommended',
    'hot',
    'highlightNote',
    'status',
    'visibleInAdminBuilder',
    'visibleInAgentBuilder',
    'startsAt',
    'expiresAt',
    'autoHideAfterExpiry',
    'archivedAt',
    'internalNotes',
    'emailDisplayName',
    'emailBullets',
    'visible',
    'quoteEligible',
    'customerEmailEligible',
    'sortOrder',
    'tvPriceAdjustment',
    'display'
];

const editableCatalogFields = [
    'displayName',
    'price',
    'regularPrice',
    'category',
    'type',
    'term',
    'devices',
    'note',
    'badges',
    'badgeColor',
    'recommended',
    'hot',
    'highlightNote',
    'status',
    'visibleInAdminBuilder',
    'visibleInAgentBuilder',
    'startsAt',
    'expiresAt',
    'autoHideAfterExpiry',
    'archivedAt',
    'internalNotes',
    'emailDisplayName',
    'emailBullets',
    'visible',
    'quoteEligible',
    'customerEmailEligible',
    'separateFlowOnly',
    'allowQuantity',
    'sortOrder',
    'display',
    'linePricing'
];

const editableTvDisplayFields = [
    'packageKey',
    'displayNameOverride',
    'cardDescription',
    'quoteSummaryNote',
    'customerEmailName',
    'customerEmailBullets',
    'pvrMode',
    'pvrPrice',
    'topBadges',
    'detailChips',
    'chips',
    'visibleInManualFallback',
    'visibleInAgentBuilder',
    'visibleInAdminBuilder',
    'sortOrder',
    'internalAgentNote',
    'customerEmailEligible',
    'emailRegularReferencePrice'
];

const emailShellTextFields = [
    { key: 'storeName', label: 'Store / dealer name', required: true },
    { key: 'storeAddressLine1', label: 'Store address line 1', required: true },
    { key: 'storeAddressLine2', label: 'Store address line 2' },
    { key: 'storePhone', label: 'Store phone', required: true },
    { key: 'introHeadline', label: 'Intro headline', multiline: true, required: true },
    { key: 'introSubheadline', label: 'Intro subheadline', multiline: true, required: true },
    { key: 'customerIntroText', label: 'Customer intro text', multiline: true, rows: 4, required: true },
    { key: 'agentContactHeadline', label: 'Agent contact headline', multiline: true, required: true },
    { key: 'agentContactSubheadline', label: 'Agent contact subheadline', multiline: true, required: true },
    { key: 'footerPrivacyText', label: 'Footer privacy label', required: true },
    { key: 'footerContactText', label: 'Footer contact label', required: true },
    { key: 'footerFindStoreText', label: 'Footer find store label', required: true },
    { key: 'footerTelusText', label: 'Footer TELUS label', required: true },
    { key: 'footerLegalText', label: 'Footer legal block', multiline: true, rows: 5, required: true },
    { key: 'footerFinePrintText', label: 'Footer fine print block', multiline: true, rows: 5, required: true }
];

const emailShellImageFields = [
    { key: 'headerImageUrl', label: 'Top authorized dealer image URL' },
    { key: 'rabbitHeroImageUrl', label: 'Rabbit hero image URL' },
    { key: 'onboardingBannerImageUrl', label: 'Onboarding/banner image URL' },
    { key: 'customerDetailsIconUrl', label: 'Customer details icon URL' },
    { key: 'modemIconUrl', label: 'Modem icon URL' },
    { key: 'tvBoxIconUrl', label: 'TV box icon URL' },
    { key: 'pvrIconUrl', label: 'PVR icon URL' },
    { key: 'starIconUrl', label: 'Star / credit icon URL' },
    { key: 'rabbitFooterImageUrl', label: 'Rabbit footer image URL' },
    { key: 'mapStoreImageUrl', label: 'Map / store footer image URL' },
    { key: 'privacyUrl', label: 'Privacy link URL' },
    { key: 'contactUrl', label: 'Contact link URL' },
    { key: 'findStoreUrl', label: 'Find a Store link URL' },
    { key: 'telusUrl', label: 'telus.com link URL' }
];

const emailShellSocialFields = [
    { key: 'neighbourhood', label: 'Neighbourhood / store icon URL' },
    { key: 'facebook', label: 'Facebook icon URL' },
    { key: 'twitter', label: 'Twitter icon URL' }
];

const homePhoneFieldLabels = {
    displayName: 'Display name',
    price: 'Promo/base price',
    regularPrice: 'Regular/reference price',
    requiredFees: 'Required fees',
    status: 'Lifecycle status',
    visibleInAdminBuilder: 'Visible in admin builder',
    visibleInAgentBuilder: 'Visible in agent builder',
    startsAt: 'Start date/time',
    expiresAt: 'Expiry date/time',
    autoHideAfterExpiry: 'Auto-hide after expiry',
    archivedAt: 'Archived at',
    internalNotes: 'Internal agent note',
    emailDisplayName: 'Customer-facing email name',
    emailBullets: 'Customer-facing email bullets',
    visible: 'Visible',
    quoteEligible: 'Quote eligible',
    customerEmailEligible: 'Customer email eligible',
    sortOrder: 'Sort order',
    display: 'Agent-facing card display'
};

const internetFieldLabels = {
    displayName: 'Display name',
    belowLinePrice: 'PAB/base price',
    aboveLinePrice: 'Regular/reference price',
    mobilityBelowLinePrice: 'Mobility discount price',
    mobilityAboveLinePrice: 'Mobility reference price',
    eppMobilityBelowLinePrice: 'EPP with mobility price',
    eppMobilityAboveLinePrice: 'EPP with mobility reference price',
    pabAdjustmentAmount: 'PAB adjustment amount',
    emailRegularPrice: 'Customer email regular price',
    mobilityDiscountEligible: 'M&H discount eligible',
    eppEligible: 'EPP discount eligible',
    badges: 'Badges',
    badgeColor: 'Badge color',
    recommended: 'Recommended',
    hot: 'Hot',
    highlightNote: 'Highlight note',
    status: 'Lifecycle status',
    visibleInAdminBuilder: 'Visible in admin builder',
    visibleInAgentBuilder: 'Visible in agent builder',
    startsAt: 'Start date/time',
    expiresAt: 'Expiry date/time',
    autoHideAfterExpiry: 'Auto-hide after expiry',
    archivedAt: 'Archived at',
    internalNotes: 'Internal agent note',
    emailDisplayName: 'Customer-facing email name',
    emailBullets: 'Customer-facing email bullets',
    visible: 'Visible',
    quoteEligible: 'Quote eligible',
    customerEmailEligible: 'Customer email eligible',
    sortOrder: 'Sort order',
    tvPriceAdjustment: 'Temporary TV price adjustment',
    display: 'Agent-facing card display'
};

const catalogFieldLabels = {
    displayName: 'Display name',
    price: 'Price',
    regularPrice: 'Regular/reference price',
    category: 'Category',
    type: 'Provider/type',
    term: 'Term',
    devices: 'Device notes',
    note: 'Internal note',
    badges: 'Badges',
    badgeColor: 'Badge color',
    recommended: 'Recommended',
    hot: 'Hot',
    highlightNote: 'Highlight note',
    status: 'Lifecycle status',
    visibleInAdminBuilder: 'Visible in admin builder',
    visibleInAgentBuilder: 'Visible in agent builder',
    startsAt: 'Start date/time',
    expiresAt: 'Expiry date/time',
    autoHideAfterExpiry: 'Auto-hide after expiry',
    archivedAt: 'Archived at',
    internalNotes: 'Internal agent note',
    emailDisplayName: 'Customer-facing email name',
    emailBullets: 'Customer-facing email bullets',
    visible: 'Visible',
    quoteEligible: 'Quote eligible',
    customerEmailEligible: 'Customer email eligible',
    separateFlowOnly: 'Separate flow only',
    allowQuantity: 'Quantity allowed',
    sortOrder: 'Sort order',
    display: 'Agent-facing card display',
    linePricing: 'Mobility line pricing rules'
};

const tvDisplayFieldLabels = {
    packageKey: 'TV V2 package key',
    displayNameOverride: 'Display name override',
    cardDescription: 'Agent-facing card note',
    quoteSummaryNote: 'Quote Summary note',
    customerEmailName: 'Customer-facing email name',
    customerEmailBullets: 'Customer-facing email bullets',
    pvrMode: 'PVR behavior',
    pvrPrice: 'PVR price',
    topBadges: 'Top card badges',
    detailChips: 'Detail chips',
    chips: 'Legacy detail chips',
    visibleInManualFallback: 'Visible in manual fallback',
    visibleInAgentBuilder: 'Visible in agent builder',
    visibleInAdminBuilder: 'Visible in admin builder',
    sortOrder: 'Sort order',
    internalAgentNote: 'Internal agent note',
    customerEmailEligible: 'Customer email eligible',
    emailRegularReferencePrice: 'Email regular/reference price'
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const valuesMatch = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const slugifyProductKey = (value, prefix = 'product') => {
    const slug = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return `${prefix}-${slug || Date.now()}`;
};

const buildUniqueProductKey = (displayName, prefix, existingIds = []) => {
    const baseKey = slugifyProductKey(displayName, prefix);
    const existing = new Set(existingIds);

    if (!existing.has(baseKey)) {
        return baseKey;
    }

    let suffix = 2;
    while (existing.has(`${baseKey}-${suffix}`)) {
        suffix += 1;
    }

    return `${baseKey}-${suffix}`;
};

const chipColor = (type) => safeChipColor(type);

const firstConfiguredChipList = (source = {}, fields = []) => {
    for (const field of fields) {
        if (Array.isArray(source[field])) {
            return normalizeDisplayChipList(source[field]);
        }
    }

    return null;
};

const sortProductsBySortOrder = (products = []) => [...products].sort((left, right) => (
    safeSortOrder(left?.sortOrder) - safeSortOrder(right?.sortOrder)
));

const mobilityAdminFilters = [
    { id: 'all', label: 'All' },
    { id: 'koodo', label: 'Koodo' },
    { id: 'telus-personal', label: 'TELUS Personal' },
    { id: 'telus-business', label: 'TELUS Business' },
    { id: 'telus-epp', label: 'TELUS EPP' }
];

const getMobilityAdminGroup = (product = {}) => {
    const searchText = [
        product.category,
        product.type,
        product.provider,
        product.displayName,
        product.id
    ].filter(Boolean).join(' ').toLowerCase();

    if (searchText.includes('epp') || searchText.includes('rbc')) {
        return 'telus-epp';
    }

    if (searchText.includes('business')) {
        return 'telus-business';
    }

    if (searchText.includes('koodo')) {
        return 'koodo';
    }

    return 'telus-personal';
};

const isDraftOnlyProduct = (product, activeProduct) => !activeProduct && (product?.isDraftOnly === true || product?.neverPublished === true);

const buildFallbackTopBadges = (product = {}) => {
    const chips = [];

    normalizeArray(product.badges).forEach((badge, index) => {
        chips.push({
            text: badge,
            type: normalizeChipType(product.badgeColor),
            visible: true,
            order: index + 1
        });
    });

    if (chips.length === 0 && product.recommended) {
        chips.push({
            text: 'Recommended',
            type: 'success',
            visible: true,
            order: 1
        });
    }

    if (chips.length === 0 && product.hot) {
        chips.push({
            text: 'HOT',
            type: 'error',
            visible: true,
            order: 1
        });
    }

    return chips;
};

const buildFallbackDetailChips = (product = {}) => {
    const chips = [];

    if (product.price !== undefined) {
        chips.push({
            text: 'Bundle promo price',
            type: 'default',
            visible: true,
            order: chips.length + 1
        });
    }

    if (Number(product.requiredFees) > 0) {
        chips.push({
            text: 'Required 911 fees included',
            type: 'default',
            visible: true,
            order: chips.length + 1
        });
    }

    if (product.highlightNote?.toLowerCase().includes('new activation')) {
        chips.push({
            text: 'New activations only',
            type: 'default',
            visible: true,
            order: chips.length + 1
        });
    }

    if (chips.length === 0 && product.id?.startsWith('ld-')) {
        chips.push({
            text: 'Long-distance add-on',
            type: 'default',
            visible: true,
            order: 1
        });
    }

    return chips;
};

const getHomePhoneDisplayMetadata = (product = {}) => {
    const display = product.display || {};
    const internalNotesText = normalizeArray(product.internalNotes).join('; ');
    const originalPrice = product.regularPrice !== undefined ? `original ${formatMoney(product.regularPrice)}` : '';
    const topBadges = firstConfiguredChipList(display, ['topBadges', 'cardBadges']) ?? buildFallbackTopBadges(product);
    const detailChips = firstConfiguredChipList(display, ['detailChips', 'featureChips', 'chips']) ?? buildFallbackDetailChips(product);

    return {
        cardDescription: display.cardDescription || display.cardSubtitle || [internalNotesText, originalPrice].filter(Boolean).join('; ') || product.note || '',
        highlightNote: display.highlightNote || product.highlightNote || '',
        internalAgentNote: display.internalAgentNote || internalNotesText || '',
        selectedBadgeText: display.selectedBadgeText || 'Selected',
        topBadges: sortedDisplayChips(topBadges),
        detailChips: sortedDisplayChips(detailChips),
        chips: sortedDisplayChips(detailChips)
    };
};

const getInternetDisplayMetadata = getHomePhoneDisplayMetadata;

const normalizeTvPriceAdjustmentEditorValue = (adjustment = {}) => {
    const amount = Number(adjustment?.amount);

    return {
        enabled: Boolean(adjustment?.enabled),
        amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
        label: String(adjustment?.label || '').trim()
    };
};

const getComparableHomePhoneValue = (product, field) => {
    if (field === 'visible') {
        return product?.visible !== false;
    }
    if (field === 'quoteEligible') {
        return product?.quoteEligible !== false;
    }
    if (field === 'customerEmailEligible') {
        return Boolean(product?.customerEmailEligible);
    }
    if (field === 'display') {
        return getHomePhoneDisplayMetadata(product);
    }
    if (field === 'status') {
        return getLifecycleStatus(product);
    }
    return product?.[field];
};

const getComparableInternetValue = (product, field) => {
    if (field === 'visible') {
        return product?.visible !== false;
    }
    if (field === 'quoteEligible') {
        return product?.quoteEligible !== false;
    }
    if (field === 'customerEmailEligible') {
        return Boolean(product?.customerEmailEligible);
    }
    if (field === 'display') {
        return getInternetDisplayMetadata(product);
    }
    if (field === 'status') {
        return getLifecycleStatus(product);
    }
    if (field === 'tvPriceAdjustment') {
        return normalizeTvPriceAdjustmentEditorValue(product?.tvPriceAdjustment);
    }
    if (field === 'linePricing') {
        return product?.linePricing ? serializeMobilityLinePricing(product.linePricing) : null;
    }

    return product?.[field];
};

const getComparableCatalogValue = (product, field) => {
    if (field === 'visible') {
        return product?.visible !== false;
    }
    if (field === 'quoteEligible') {
        return product?.quoteEligible !== false;
    }
    if (field === 'customerEmailEligible') {
        return Boolean(product?.customerEmailEligible);
    }
    if (field === 'separateFlowOnly') {
        return Boolean(product?.separateFlowOnly);
    }
    if (field === 'allowQuantity') {
        return Boolean(product?.allowQuantity);
    }
    if (field === 'display') {
        return getHomePhoneDisplayMetadata(product);
    }
    if (field === 'status') {
        return getLifecycleStatus(product);
    }

    return product?.[field];
};

const numberFromInput = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const linesFromText = (value) => String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const categoryTabs = [
    { id: 'overview', label: 'Overview', icon: <ViewIcon /> },
    { id: 'internet', label: 'Internet', icon: <InternetIcon /> },
    { id: 'tv', label: 'TV V2 Reference', icon: <TvIcon /> },
    { id: 'tvDisplay', label: 'TV Display', icon: <TvIcon /> },
    { id: 'homePhone', label: 'Home Phone', icon: <HomeIcon /> },
    { id: 'mobility', label: 'Mobility', icon: <MobilityIcon /> },
    { id: 'security', label: 'Security', icon: <SecurityIcon /> },
    { id: 'addons', label: 'Add-ons', icon: <AddonIcon /> },
    { id: 'discounts', label: 'Discount Rules', icon: <DiscountIcon /> },
    { id: 'email', label: 'Email Shell', icon: <EmailIcon /> },
    { id: 'versions', label: 'Version History', icon: <HistoryIcon /> }
];

const futureControlCards = [
    {
        title: 'Product Catalog',
        body: 'Future controls for product visibility, order, badges, customer notes, and quote eligibility.',
        chips: ['Order', 'Badges', 'Hide/show', 'Quote rules']
    },
    {
        title: 'Pricing Rules',
        body: 'Future controls for PAB, TELUS/Koodo mobility, EPP, one-time credits, and required fees.',
        chips: ['PAB', 'M&H', 'EPP', 'Credits']
    },
    {
        title: 'Email Output',
        body: 'Future controls for customer-facing product names, bullets, shell images, store text, and legal text.',
        chips: ['Email names', 'Bullets', 'Shell', 'Legal']
    },
    {
        title: 'Draft / Publish',
        body: 'Future workflow for draft edits, validation, publish, version history, rollback, and audit logs.',
        chips: ['Draft', 'Validate', 'Publish', 'Rollback']
    }
];

const tvToolLinks = [
    { label: 'Open TV Package Pricing', href: '/offers/tv-pricing-v2' },
    { label: 'Open TV Package Calculator', href: '/offers/tv-packages-v2' },
    { label: 'Open Theme Pack Editor', href: '/offers/theme-pack-editor' }
];

const collectProductEntries = (sourceConfig) => {
    const entries = [];
    const addProducts = (category, products = []) => {
        normalizeArray(products).forEach((product) => {
            entries.push({ category, product });
        });
    };

    addProducts('Internet', sourceConfig?.products?.internet);
    addProducts('Home Phone plans', sourceConfig?.products?.homePhone?.plans);
    addProducts('Home Phone add-ons', sourceConfig?.products?.homePhone?.longDistanceAddons);
    addProducts('Mobility', sourceConfig?.products?.mobility?.products);
    addProducts('Security', sourceConfig?.products?.security?.products);

    normalizeArray(sourceConfig?.products?.addons?.groups).forEach((group) => {
        addProducts(`Add-ons / ${group.title || group.id || 'group'}`, group.items);
    });

    return entries;
};

function ProductPricingManagerWorkspace({
    activeTab,
    setActiveTab,
    pricingConfig,
    draftWorkflow
}) {
    const {
        config,
        draftConfig,
        storageStatus,
        draftStatus,
        versions,
        tvV2Pricing,
        loading,
        error,
        loadConfig
    } = pricingConfig;
    const {
        editingInternet,
        setEditingInternet,
        savingInternetDraft,
        setSavingInternetDraft,
        editingHomePhone,
        setEditingHomePhone,
        savingHomePhoneDraft: savingDraft,
        setSavingHomePhoneDraft: setSavingDraft,
        editingCatalog,
        setEditingCatalog,
        savingCatalogDraft,
        setSavingCatalogDraft,
        editingTvDisplay,
        setEditingTvDisplay,
        savingTvDisplayDraft,
        setSavingTvDisplayDraft,
        editingEmailShell,
        setEditingEmailShell,
        savingEmailShellDraft,
        setSavingEmailShellDraft,
        emailShellPreviewOpen,
        setEmailShellPreviewOpen
    } = useProductEditorState();
    const [showMoreVersions, setShowMoreVersions] = useState(false);
    const [showAllDraftWarnings, setShowAllDraftWarnings] = useState(false);
    const [showAllValidationWarnings, setShowAllValidationWarnings] = useState(false);
    const [versionHistoryScope, setVersionHistoryScope] = useState('homePhone');
    const [mobilityAdminFilter, setMobilityAdminFilter] = useState('all');
    const {
        draftMessage,
        setDraftMessage,
        draftError,
        setDraftError,
        draftValidation,
        validatingDraft,
        validateDraftConfig,
        saveScopedDraftConfig,
        publishDialogOpen,
        setPublishDialogOpen,
        publishScope,
        publishSummary,
        setPublishSummary,
        publishing,
        openPublishDialog,
        publishDraft,
        rollbackTarget,
        setRollbackTarget,
        rollbackScope,
        rollbackSummary,
        setRollbackSummary,
        rollingBack,
        openRollbackDialog,
        rollbackConfig
    } = draftWorkflow;

    const counts = useMemo(() => {
        if (!config) {
            return {
                internet: 0,
                homePhone: 0,
                mobility: 0,
                security: 0,
                addons: 0
            };
        }

        return {
            internet: config.products?.internet?.length || 0,
            homePhone: (config.products?.homePhone?.plans?.length || 0) + (config.products?.homePhone?.longDistanceAddons?.length || 0),
            mobility: config.products?.mobility?.products?.length || 0,
            security: config.products?.security?.products?.length || 0,
            addons: (config.products?.addons?.groups || []).reduce((total, group) => total + (group.items?.length || 0), 0)
        };
    }, [config]);

    const getTvDisplayPackages = useCallback((sourceConfig) => sourceConfig?.tvDisplaySettings?.packages || [], []);

    const draftIntegrityWarnings = useMemo(() => {
        const warnings = [];

        collectProductEntries(draftConfig).forEach(({ category, product }) => {
            if (!product || typeof product !== 'object') {
                warnings.push(`${category} has an invalid product row.`);
                return;
            }

            const productLabel = `${category} ${product.id || product.displayName || 'product'}`;

            if (!isValidSortOrderInput(product.sortOrder)) {
                warnings.push(`${productLabel} has invalid sortOrder.`);
            }

            if (product.badges !== undefined && !Array.isArray(product.badges)) {
                warnings.push(`${productLabel} badges must be an array to render badge chips.`);
            }

            const badgeColorIssue = getChipColorIssue(product.badgeColor);
            if (badgeColorIssue) {
                warnings.push(`${productLabel} badgeColor ${badgeColorIssue}`);
            }

            ['chips', 'topBadges', 'cardBadges', 'detailChips', 'featureChips'].forEach((fieldName) => {
                normalizeArray(product.display?.[fieldName]).forEach((chip, index) => {
                    const chipColorIssue = getChipColorIssue(chip.type || chip.color);

                    if (chipColorIssue) {
                        warnings.push(`${productLabel} ${fieldName} chip ${index + 1} ${chipColorIssue}`);
                    }
                });
            });
        });

        getTvDisplayPackages(draftConfig).forEach((pkg) => {
            const productLabel = `TV Display ${pkg.packageKey || 'package'}`;

            if (!isValidSortOrderInput(pkg.sortOrder)) {
                warnings.push(`${productLabel} has invalid sortOrder.`);
            }

            ['chips', 'topBadges', 'cardBadges', 'detailChips', 'featureChips'].forEach((fieldName) => {
                normalizeArray(pkg[fieldName]).forEach((chip, index) => {
                    const chipColorIssue = getChipColorIssue(chip.type || chip.color);

                    if (chipColorIssue) {
                        warnings.push(`${productLabel} ${fieldName} chip ${index + 1} ${chipColorIssue}`);
                    }
                });
            });
        });

        return warnings;
    }, [draftConfig, getTvDisplayPackages]);

    const sortedVersions = useMemo(() => (
        [...versions].sort((left, right) => Number(right.versionNumber || 0) - Number(left.versionNumber || 0))
    ), [versions]);

    const getHomePhoneGroup = (sourceConfig, groupName) => sourceConfig?.products?.homePhone?.[groupName] || [];
    const getInternetProducts = (sourceConfig) => sourceConfig?.products?.internet || [];
    const getCatalogProducts = (sourceConfig, scope, groupId = null) => {
        if (scope === 'mobility') {
            return sourceConfig?.products?.mobility?.products || [];
        }
        if (scope === 'security') {
            return sourceConfig?.products?.security?.products || [];
        }
        if (scope === 'addons') {
            const group = (sourceConfig?.products?.addons?.groups || []).find((candidate) => candidate.id === groupId);
            return group?.items || [];
        }

        return [];
    };
    const getEmailShell = (sourceConfig) => sourceConfig?.emailShell || {};
    const activeEmailShell = getEmailShell(config);
    const draftEmailShell = getEmailShell(draftConfig || config);
    const emailShellDraftChanged = !valuesMatch(activeEmailShell, draftEmailShell);
    const tvV2PriceByKey = useMemo(() => {
        const lookup = {};

        normalizeArray(tvV2Pricing.packages).forEach((pkg) => {
            if (pkg.package_key) {
                lookup[pkg.package_key] = pkg.price;
            }
        });

        Object.entries(tvV2Pricing.priceTable || {}).forEach(([key, value]) => {
            if (lookup[key] === undefined) {
                lookup[key] = value;
            }
        });

        return lookup;
    }, [tvV2Pricing]);

    const findActiveInternetProduct = (productId) => (
        getInternetProducts(config).find((product) => product.id === productId)
    );

    const findActiveHomePhoneProduct = (groupName, productId) => (
        getHomePhoneGroup(config, groupName).find((product) => product.id === productId)
    );
    const findActiveCatalogProduct = (scope, productId, groupId = null) => (
        getCatalogProducts(config, scope, groupId).find((product) => product.id === productId)
    );
    const findActiveTvDisplayPackage = (packageKey) => (
        getTvDisplayPackages(config).find((pkg) => pkg.packageKey === packageKey)
    );

    const getExistingProductIds = (scope, groupNameOrId = null) => {
        if (scope === 'internet') {
            return getInternetProducts(draftConfig || config).map((product) => product.id);
        }

        if (scope === 'homePhone') {
            return getHomePhoneGroup(draftConfig || config, groupNameOrId || 'plans').map((product) => product.id);
        }

        return getCatalogProducts(draftConfig || config, scope, groupNameOrId).map((product) => product.id);
    };

    const isInternetDraftChanged = (draftProduct, activeProduct) => (
        editableInternetFields.some((field) => !valuesMatch(
            getComparableInternetValue(draftProduct, field),
            getComparableInternetValue(activeProduct, field)
        ))
    );

    const getInternetChangedFields = (draftProduct, activeProduct) => editableInternetFields
        .filter((field) => !valuesMatch(
            getComparableInternetValue(draftProduct, field),
            getComparableInternetValue(activeProduct, field)
        ))
        .map((field) => internetFieldLabels[field] || field);

    const isHomePhoneDraftChanged = (draftProduct, activeProduct) => (
        editableHomePhoneFields.some((field) => !valuesMatch(
            getComparableHomePhoneValue(draftProduct, field),
            getComparableHomePhoneValue(activeProduct, field)
        ))
    );

    const getHomePhoneChangedFields = (draftProduct, activeProduct) => editableHomePhoneFields
        .filter((field) => !valuesMatch(
            getComparableHomePhoneValue(draftProduct, field),
            getComparableHomePhoneValue(activeProduct, field)
        ))
        .map((field) => homePhoneFieldLabels[field] || field);

    const isCatalogDraftChanged = (draftProduct, activeProduct) => (
        editableCatalogFields.some((field) => !valuesMatch(
            getComparableCatalogValue(draftProduct, field),
            getComparableCatalogValue(activeProduct, field)
        ))
    );

    const isTvDisplayDraftChanged = (draftPackage, activePackage) => (
        editableTvDisplayFields.some((field) => !valuesMatch(
            draftPackage?.[field] ?? null,
            activePackage?.[field] ?? null
        ))
    );

    const hasDraftChangesForScope = (scope) => {
        if (!draftConfig || !config) {
            return false;
        }

        if (scope === 'internet') {
            return getInternetProducts(draftConfig).some((product) => isInternetDraftChanged(product, findActiveInternetProduct(product.id)));
        }

        if (scope === 'homePhone') {
            return ['plans', 'longDistanceAddons'].some((groupName) => (
                getHomePhoneGroup(draftConfig, groupName).some((product) => isHomePhoneDraftChanged(product, findActiveHomePhoneProduct(groupName, product.id)))
            ));
        }

        if (scope === 'addons') {
            return (draftConfig.products?.addons?.groups || []).some((group) => (
                (group.items || []).some((product) => isCatalogDraftChanged(product, findActiveCatalogProduct(scope, product.id, group.id)))
            ));
        }

        if (scope === 'emailShell') {
            return emailShellDraftChanged;
        }

        if (scope === 'tvDisplaySettings') {
            return getTvDisplayPackages(draftConfig || config).some((pkg) => isTvDisplayDraftChanged(pkg, findActiveTvDisplayPackage(pkg.packageKey)));
        }

        return getCatalogProducts(draftConfig, scope).some((product) => isCatalogDraftChanged(product, findActiveCatalogProduct(scope, product.id)));
    };

    const getCatalogChangedFields = (draftProduct, activeProduct) => editableCatalogFields
        .filter((field) => !valuesMatch(
            getComparableCatalogValue(draftProduct, field),
            getComparableCatalogValue(activeProduct, field)
        ))
        .map((field) => catalogFieldLabels[field] || field);

    const getTvDisplayChangedFields = (draftPackage, activePackage) => editableTvDisplayFields
        .filter((field) => !valuesMatch(draftPackage?.[field] ?? null, activePackage?.[field] ?? null))
        .map((field) => tvDisplayFieldLabels[field] || field);

    const openInternetEditor = (product) => {
        const display = getInternetDisplayMetadata(product);

        setDraftError('');
        setDraftMessage('');
        setEditingInternet({
            originalId: product.id,
            values: {
                displayName: product.displayName || '',
                belowLinePrice: product.belowLinePrice ?? 0,
                aboveLinePrice: product.aboveLinePrice ?? '',
                mobilityBelowLinePrice: product.mobilityBelowLinePrice ?? 0,
                mobilityAboveLinePrice: product.mobilityAboveLinePrice ?? '',
                eppMobilityBelowLinePrice: product.eppMobilityBelowLinePrice ?? 0,
                eppMobilityAboveLinePrice: product.eppMobilityAboveLinePrice ?? '',
                pabAdjustmentAmount: product.pabAdjustmentAmount ?? 10,
                emailRegularPrice: product.emailRegularPrice ?? '',
                mobilityDiscountEligible: product.mobilityDiscountEligible !== false,
                eppEligible: product.eppEligible !== false,
                badgeText: normalizeArray(product.badges).join('\n'),
                badgeColor: safeChipColor(product.badgeColor),
                recommended: Boolean(product.recommended),
                hot: Boolean(product.hot),
                highlightNote: product.highlightNote || '',
                status: getLifecycleStatus(product),
                visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
                visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
                startsAt: normalizeDateTimeInput(product.startsAt),
                expiresAt: normalizeDateTimeInput(product.expiresAt),
                autoHideAfterExpiry: product.autoHideAfterExpiry !== false,
                archivedAt: product.archivedAt || '',
                isDraftOnly: Boolean(product.isDraftOnly),
                neverPublished: Boolean(product.neverPublished),
                internalNotesText: normalizeArray(product.internalNotes).join('\n'),
                emailDisplayName: product.emailDisplayName || '',
                emailBulletsText: normalizeArray(product.emailBullets).join('\n'),
                visible: product.visible !== false,
                quoteEligible: product.quoteEligible !== false,
                customerEmailEligible: Boolean(product.customerEmailEligible),
                sortOrder: product.sortOrder ?? 0,
                tvPriceAdjustment: normalizeTvPriceAdjustmentEditorValue(product.tvPriceAdjustment),
                cardDescription: display.cardDescription,
                displayHighlightNote: display.highlightNote,
                internalAgentNote: display.internalAgentNote,
                selectedBadgeText: display.selectedBadgeText,
                topBadges: display.topBadges,
                displayChips: display.detailChips
            }
        });
    };

    const openNewInternetEditor = () => {
        const displayName = window.prompt('Display name for the new Internet product');
        if (!displayName || !displayName.trim()) {
            return;
        }

        const product = {
            id: buildUniqueProductKey(displayName, 'internet', getExistingProductIds('internet')),
            displayName: displayName.trim(),
            belowLinePrice: 0,
            aboveLinePrice: 0,
            mobilityBelowLinePrice: 0,
            mobilityAboveLinePrice: 0,
            eppMobilityBelowLinePrice: 0,
            eppMobilityAboveLinePrice: 0,
            pabAdjustmentAmount: 10,
            emailRegularPrice: '',
            mobilityDiscountEligible: true,
            eppEligible: true,
            badges: ['Draft new'],
            badgeColor: 'info',
            visible: true,
            quoteEligible: true,
            customerEmailEligible: false,
            sortOrder: getInternetProducts(draftConfig || config).length * 10 + 10,
            status: 'active',
            visibleInAdminBuilder: true,
            visibleInAgentBuilder: true,
            autoHideAfterExpiry: true,
            isDraftOnly: true,
            neverPublished: true,
            tvPriceAdjustment: {
                enabled: false,
                amount: 0,
                label: ''
            },
            emailDisplayName: displayName.trim(),
            emailBullets: [],
            display: {
                cardDescription: '',
                highlightNote: '',
                internalAgentNote: '',
                selectedBadgeText: 'Selected',
                topBadges: [{ text: 'Draft new', type: 'info', visible: true, order: 1 }],
                detailChips: [],
                chips: []
            }
        };

        openInternetEditor(product);
        setEditingInternet((current) => ({ ...current, isNew: true }));
    };

    const updateInternetEditorValue = (field, value) => {
        setEditingInternet((current) => ({
            ...current,
            values: {
                ...current.values,
                [field]: value
            }
        }));
    };

    const updateInternetTvPriceAdjustmentValue = (field, value) => {
        setEditingInternet((current) => ({
            ...current,
            values: {
                ...current.values,
                tvPriceAdjustment: {
                    ...(current.values.tvPriceAdjustment || {}),
                    [field]: value
                }
            }
        }));
    };

    const updateEditorChipList = (setEditor, listField, index, field, value) => {
        setEditor((current) => {
            const chips = [...(current.values[listField] || [])];
            chips[index] = {
                ...chips[index],
                [field]: field === 'order' ? numberFromInput(value, chips[index].order) : value
            };

            return {
                ...current,
                values: {
                    ...current.values,
                    [listField]: chips
                }
            };
        });
    };

    const addEditorChipListItem = (setEditor, listField, label = 'New chip') => {
        setEditor((current) => ({
            ...current,
            values: {
                ...current.values,
                [listField]: [
                    ...(current.values[listField] || []),
                    {
                        text: label,
                        type: 'default',
                        visible: true,
                        order: (current.values[listField] || []).length + 1
                    }
                ]
            }
        }));
    };

    const removeEditorChipListItem = (setEditor, listField, index) => {
        setEditor((current) => ({
            ...current,
            values: {
                ...current.values,
                [listField]: (current.values[listField] || []).filter((_, chipIndex) => chipIndex !== index)
            }
        }));
    };

    const updateInternetTopBadge = (index, field, value) => updateEditorChipList(setEditingInternet, 'topBadges', index, field, value);
    const addInternetTopBadge = () => addEditorChipListItem(setEditingInternet, 'topBadges', 'New badge');
    const removeInternetTopBadge = (index) => removeEditorChipListItem(setEditingInternet, 'topBadges', index);

    const updateInternetDisplayChip = (index, field, value) => {
        updateEditorChipList(setEditingInternet, 'displayChips', index, field, value);
    };

    const addInternetDisplayChip = () => {
        addEditorChipListItem(setEditingInternet, 'displayChips');
    };

    const removeInternetDisplayChip = (index) => removeEditorChipListItem(setEditingInternet, 'displayChips', index);

    const openHomePhoneEditor = (groupName, product) => {
        const display = getHomePhoneDisplayMetadata(product);

        setDraftError('');
        setDraftMessage('');
        setEditingHomePhone({
            groupName,
            originalId: product.id,
            values: {
                displayName: product.displayName || '',
                price: product.price ?? 0,
                regularPrice: product.regularPrice ?? '',
                requiredFees: product.requiredFees ?? '',
                status: getLifecycleStatus(product),
                visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
                visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
                startsAt: normalizeDateTimeInput(product.startsAt),
                expiresAt: normalizeDateTimeInput(product.expiresAt),
                autoHideAfterExpiry: product.autoHideAfterExpiry !== false,
                archivedAt: product.archivedAt || '',
                isDraftOnly: Boolean(product.isDraftOnly),
                neverPublished: Boolean(product.neverPublished),
                internalNotesText: normalizeArray(product.internalNotes).join('\n'),
                emailDisplayName: product.emailDisplayName || '',
                emailBulletsText: normalizeArray(product.emailBullets).join('\n'),
                visible: product.visible !== false,
                quoteEligible: product.quoteEligible !== false,
                customerEmailEligible: Boolean(product.customerEmailEligible),
                sortOrder: product.sortOrder ?? 0,
                cardDescription: display.cardDescription,
                displayHighlightNote: display.highlightNote,
                internalAgentNote: display.internalAgentNote,
                selectedBadgeText: display.selectedBadgeText,
                topBadges: display.topBadges,
                displayChips: display.detailChips
            }
        });
    };

    const openNewHomePhoneEditor = (groupName = 'plans') => {
        const displayName = window.prompt(`Display name for the new ${groupName === 'plans' ? 'Home Phone plan' : 'Home Phone add-on'}`);
        if (!displayName || !displayName.trim()) {
            return;
        }

        const product = {
            id: buildUniqueProductKey(displayName, groupName === 'plans' ? 'phone' : 'ld', getExistingProductIds('homePhone', groupName)),
            displayName: displayName.trim(),
            price: 0,
            regularPrice: 0,
            requiredFees: groupName === 'plans' ? 0.89 : 0,
            badges: ['Draft new'],
            badgeColor: 'info',
            visible: true,
            quoteEligible: true,
            customerEmailEligible: false,
            sortOrder: getHomePhoneGroup(draftConfig || config, groupName).length * 10 + 10,
            status: 'active',
            visibleInAdminBuilder: true,
            visibleInAgentBuilder: true,
            autoHideAfterExpiry: true,
            isDraftOnly: true,
            neverPublished: true,
            emailDisplayName: displayName.trim(),
            emailBullets: [],
            display: {
                cardDescription: '',
                highlightNote: '',
                internalAgentNote: '',
                selectedBadgeText: 'Selected',
                topBadges: [{ text: 'Draft new', type: 'info', visible: true, order: 1 }],
                detailChips: [],
                chips: []
            }
        };

        openHomePhoneEditor(groupName, product);
        setEditingHomePhone((current) => ({ ...current, isNew: true }));
    };

    const updateEditorValue = (field, value) => {
        setEditingHomePhone((current) => ({
            ...current,
            values: {
                ...current.values,
                [field]: value
            }
        }));
    };

    const updateHomePhoneTopBadge = (index, field, value) => updateEditorChipList(setEditingHomePhone, 'topBadges', index, field, value);
    const addHomePhoneTopBadge = () => addEditorChipListItem(setEditingHomePhone, 'topBadges', 'New badge');
    const removeHomePhoneTopBadge = (index) => removeEditorChipListItem(setEditingHomePhone, 'topBadges', index);

    const updateDisplayChip = (index, field, value) => {
        updateEditorChipList(setEditingHomePhone, 'displayChips', index, field, value);
    };

    const addDisplayChip = () => {
        addEditorChipListItem(setEditingHomePhone, 'displayChips');
    };

    const removeDisplayChip = (index) => removeEditorChipListItem(setEditingHomePhone, 'displayChips', index);

    const openCatalogEditor = (scope, product, groupId = null) => {
        const display = getHomePhoneDisplayMetadata(product);

        setDraftError('');
        setDraftMessage('');
        setEditingCatalog({
            scope,
            groupId,
            originalId: product.id,
            values: {
                displayName: product.displayName || '',
                price: product.price ?? 0,
                regularPrice: product.regularPrice ?? '',
                category: product.category || '',
                type: product.type || '',
                term: product.term || '',
                devices: product.devices || '',
                note: product.note || '',
                badgeText: normalizeArray(product.badges).join('\n'),
                badgeColor: safeChipColor(product.badgeColor),
                recommended: Boolean(product.recommended),
                hot: Boolean(product.hot),
                highlightNote: product.highlightNote || '',
                status: getLifecycleStatus(product),
                visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
                visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
                startsAt: normalizeDateTimeInput(product.startsAt),
                expiresAt: normalizeDateTimeInput(product.expiresAt),
                autoHideAfterExpiry: product.autoHideAfterExpiry !== false,
                archivedAt: product.archivedAt || '',
                isDraftOnly: Boolean(product.isDraftOnly),
                neverPublished: Boolean(product.neverPublished),
                internalNotesText: normalizeArray(product.internalNotes).join('\n'),
                emailDisplayName: product.emailDisplayName || '',
                emailBulletsText: normalizeArray(product.emailBullets).join('\n'),
                visible: product.visible !== false,
                quoteEligible: product.quoteEligible !== false,
                customerEmailEligible: Boolean(product.customerEmailEligible),
                separateFlowOnly: Boolean(product.separateFlowOnly),
                allowQuantity: Boolean(product.allowQuantity),
                sortOrder: product.sortOrder ?? 0,
                cardDescription: display.cardDescription,
                displayHighlightNote: display.highlightNote,
                internalAgentNote: display.internalAgentNote,
                selectedBadgeText: display.selectedBadgeText,
                topBadges: display.topBadges,
                displayChips: display.detailChips,
                linePricing: scope === 'mobility' ? normalizeMobilityLinePricingForEditor(product) : null
            }
        });
    };

    const openNewCatalogEditor = (scope, groupId = null) => {
        const displayName = window.prompt(`Display name for the new ${scopeLabel(scope)} product`);
        if (!displayName || !displayName.trim()) {
            return;
        }

        const categories = draftConfig?.products?.mobility?.categories || config?.products?.mobility?.categories || [];
        const product = {
            id: buildUniqueProductKey(displayName, scope === 'addons' ? 'addon' : scope, getExistingProductIds(scope, groupId)),
            displayName: displayName.trim(),
            price: 0,
            regularPrice: 0,
            category: scope === 'mobility' ? categories[0]?.id || '' : undefined,
            type: scope === 'mobility' ? categories[0]?.label || '' : undefined,
            badges: ['Draft new'],
            badgeColor: 'info',
            visible: true,
            quoteEligible: true,
            customerEmailEligible: false,
            separateFlowOnly: scope === 'security',
            allowQuantity: false,
            sortOrder: getCatalogProducts(draftConfig || config, scope, groupId).length * 10 + 10,
            status: 'active',
            visibleInAdminBuilder: true,
            visibleInAgentBuilder: true,
            autoHideAfterExpiry: true,
            isDraftOnly: true,
            neverPublished: true,
            emailDisplayName: displayName.trim(),
            emailBullets: [],
            linePricing: scope === 'mobility'
                ? {
                    mode: 'flatPerLine',
                    flatPricePerLine: 0,
                    firstLinePrice: 0,
                    additionalLinePrice: 0,
                    tiers: [{ minLines: 1, maxLines: '', pricePerLine: 0, label: 'All lines' }],
                    maxLines: '',
                    noteTemplate: ''
                }
                : undefined,
            display: {
                cardDescription: '',
                highlightNote: '',
                internalAgentNote: '',
                selectedBadgeText: 'Selected',
                topBadges: [{ text: 'Draft new', type: 'info', visible: true, order: 1 }],
                detailChips: [],
                chips: []
            }
        };

        openCatalogEditor(scope, product, groupId);
        setEditingCatalog((current) => ({ ...current, isNew: true }));
    };

    const updateCatalogEditorValue = (field, value) => {
        setEditingCatalog((current) => ({
            ...current,
            values: {
                ...current.values,
                [field]: value
            }
        }));
    };

    const updateCatalogTopBadge = (index, field, value) => updateEditorChipList(setEditingCatalog, 'topBadges', index, field, value);
    const addCatalogTopBadge = () => addEditorChipListItem(setEditingCatalog, 'topBadges', 'New badge');
    const removeCatalogTopBadge = (index) => removeEditorChipListItem(setEditingCatalog, 'topBadges', index);

    const updateCatalogDisplayChip = (index, field, value) => {
        updateEditorChipList(setEditingCatalog, 'displayChips', index, field, value);
    };

    const addCatalogDisplayChip = () => {
        addEditorChipListItem(setEditingCatalog, 'displayChips');
    };

    const removeCatalogDisplayChip = (index) => removeEditorChipListItem(setEditingCatalog, 'displayChips', index);

    const updateCatalogLinePricing = (field, value) => {
        setEditingCatalog((current) => ({
            ...current,
            values: {
                ...current.values,
                linePricing: {
                    ...(current.values.linePricing || {}),
                    [field]: value
                }
            }
        }));
    };

    const updateCatalogLinePricingTier = (index, field, value) => {
        setEditingCatalog((current) => {
            const tiers = [...(current.values.linePricing?.tiers || [])];
            tiers[index] = {
                ...tiers[index],
                [field]: value
            };

            return {
                ...current,
                values: {
                    ...current.values,
                    linePricing: {
                        ...(current.values.linePricing || {}),
                        tiers
                    }
                }
            };
        });
    };

    const addCatalogLinePricingTier = () => {
        setEditingCatalog((current) => ({
            ...current,
            values: {
                ...current.values,
                linePricing: {
                    ...(current.values.linePricing || {}),
                    tiers: [
                        ...(current.values.linePricing?.tiers || []),
                        { minLines: 1, maxLines: '', pricePerLine: 0, label: '' }
                    ]
                }
            }
        }));
    };

    const removeCatalogLinePricingTier = (index) => {
        setEditingCatalog((current) => ({
            ...current,
            values: {
                ...current.values,
                linePricing: {
                    ...(current.values.linePricing || {}),
                    tiers: (current.values.linePricing?.tiers || []).filter((_, tierIndex) => tierIndex !== index)
                }
            }
        }));
    };

    const openTvDisplayEditor = (pkg) => {
        setDraftError('');
        setDraftMessage('');
        setEditingTvDisplay({
            packageKey: pkg.packageKey,
            values: {
                packageKey: pkg.packageKey || '',
                displayNameOverride: pkg.displayNameOverride || '',
                cardDescription: pkg.cardDescription || '',
                quoteSummaryNote: pkg.quoteSummaryNote || '',
                customerEmailName: pkg.customerEmailName || '',
                customerEmailBulletsText: normalizeArray(pkg.customerEmailBullets).join('\n'),
                pvrMode: pkg.pvrMode || 'optional',
                pvrPrice: pkg.pvrPrice ?? 0,
                visibleInManualFallback: pkg.visibleInManualFallback !== false,
                visibleInAgentBuilder: pkg.visibleInAgentBuilder !== false,
                visibleInAdminBuilder: pkg.visibleInAdminBuilder !== false,
                sortOrder: pkg.sortOrder ?? 999,
                internalAgentNote: pkg.internalAgentNote || '',
                customerEmailEligible: pkg.customerEmailEligible !== false,
                emailRegularReferencePrice: pkg.emailRegularReferencePrice ?? '',
                topBadges: sortedDisplayChips(
                    (firstConfiguredChipList(pkg, ['topBadges', 'cardBadges']) ?? []).map(normalizeDisplayChipEntry)
                ),
                displayChips: sortedDisplayChips(
                    (firstConfiguredChipList(pkg, ['detailChips', 'featureChips', 'chips']) ?? []).map(normalizeDisplayChipEntry)
                )
            }
        });
    };

    const updateTvDisplayEditorValue = (field, value) => {
        setEditingTvDisplay((current) => ({
            ...current,
            values: {
                ...current.values,
                [field]: value
            }
        }));
    };

    const updateTvTopBadge = (index, field, value) => updateEditorChipList(setEditingTvDisplay, 'topBadges', index, field, value);
    const addTvTopBadge = () => addEditorChipListItem(setEditingTvDisplay, 'topBadges', 'New badge');
    const removeTvTopBadge = (index) => removeEditorChipListItem(setEditingTvDisplay, 'topBadges', index);

    const updateTvDisplayChip = (index, field, value) => {
        updateEditorChipList(setEditingTvDisplay, 'displayChips', index, field, value);
    };

    const addTvDisplayChip = () => {
        addEditorChipListItem(setEditingTvDisplay, 'displayChips');
    };

    const removeTvDisplayChip = (index) => removeEditorChipListItem(setEditingTvDisplay, 'displayChips', index);

    const openEmailShellEditor = () => {
        setDraftError('');
        setDraftMessage('');
        setEditingEmailShell(JSON.parse(JSON.stringify(draftEmailShell || {})));
    };

    const updateEmailShellValue = (field, value) => {
        setEditingEmailShell((current) => ({
            ...current,
            [field]: value
        }));
    };

    const updateEmailShellSocialValue = (field, value) => {
        setEditingEmailShell((current) => ({
            ...current,
            socialIconUrls: {
                ...(current.socialIconUrls || {}),
                [field]: value
            }
        }));
    };

    const isHttpUrl = (value) => {
        try {
            const parsedUrl = new URL(String(value || '').trim());
            return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
        } catch (err) {
            return false;
        }
    };

    const buildUpdatedHomePhoneConfig = () => serializeHomePhoneEditor(
        draftConfig || config,
        editingHomePhone
    );

    const buildUpdatedEmailShellConfig = () => serializeEmailShellEditor(
        draftConfig || config,
        editingEmailShell
    );

    const buildUpdatedInternetConfig = () => serializeInternetEditor(
        draftConfig || config,
        editingInternet
    );

    const buildUpdatedCatalogConfig = () => {
        const sourceConfig = draftConfig || config;
        if (editingCatalog.scope === 'mobility') {
            return serializeMobilityEditor(sourceConfig, editingCatalog);
        }
        if (editingCatalog.scope === 'security') {
            return serializeSecurityEditor(sourceConfig, editingCatalog);
        }
        return serializeAddonsEditor(sourceConfig, editingCatalog);
    };

    const buildUpdatedTvDisplayConfig = () => serializeTvDisplayEditor(
        draftConfig || config,
        editingTvDisplay
    );

    const saveInternetDraft = async () => {
        if (!editingInternet) {
            return;
        }

        setSavingInternetDraft(true);
        setDraftError('');
        setDraftMessage('');

        try {
            const localErrors = validateInternetEditor(editingInternet.values);
            if (localErrors.length > 0) {
                throw new Error(localErrors.slice(0, 3).join(' '));
            }

            const updatedConfig = buildUpdatedInternetConfig();
            const { response, data } = await savePricingDraft(
                updatedConfig,
                'internet',
                `Internet draft updated: ${editingInternet.originalId}`
            );

            if (!response.ok || data.saved === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `Internet draft save failed with status ${response.status}`);
            }

            setEditingInternet(null);
            setDraftMessage('Internet draft saved. Publish Internet Draft before Bundle Builder sees these values.');
            await loadConfig({ showLoading: false });
        } catch (err) {
            setDraftError(err.message || 'Failed to save Internet draft.');
        } finally {
            setSavingInternetDraft(false);
        }
    };

    const saveHomePhoneDraft = async () => {
        if (!editingHomePhone) {
            return;
        }

        setSavingDraft(true);
        setDraftError('');
        setDraftMessage('');

        try {
            const localErrors = validateHomePhoneEditor(editingHomePhone.values);
            if (localErrors.length > 0) {
                throw new Error(localErrors.slice(0, 3).join(' '));
            }

            const updatedConfig = buildUpdatedHomePhoneConfig();
            const { response, data } = await savePricingDraft(
                updatedConfig,
                null,
                `Home Phone draft updated: ${editingHomePhone.originalId}`
            );

            if (!response.ok || data.saved === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `Draft save failed with status ${response.status}`);
            }

            setEditingHomePhone(null);
            setDraftMessage('Home Phone draft saved. Publish Home Phone Draft before Bundle Builder sees these values.');
            await loadConfig({ showLoading: false });
        } catch (err) {
            setDraftError(err.message || 'Failed to save Home Phone draft.');
        } finally {
            setSavingDraft(false);
        }
    };

    const saveCatalogDraft = async () => {
        if (!editingCatalog) {
            return;
        }

        setSavingCatalogDraft(true);
        setDraftError('');
        setDraftMessage('');

        try {
            const localErrors = editingCatalog.scope === 'mobility'
                ? validateMobilityEditor(editingCatalog.values)
                : editingCatalog.scope === 'security'
                    ? validateSecurityEditor(editingCatalog.values)
                    : validateAddonsEditor(editingCatalog.values);
            if (localErrors.length > 0) {
                throw new Error(localErrors.slice(0, 3).join(' '));
            }

            const updatedConfig = buildUpdatedCatalogConfig();
            const { response, data } = await savePricingDraft(
                updatedConfig,
                editingCatalog.scope,
                `${scopeLabel(editingCatalog.scope)} draft updated: ${editingCatalog.originalId}`
            );

            if (!response.ok || data.saved === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `${scopeLabel(editingCatalog.scope)} draft save failed with status ${response.status}`);
            }

            setEditingCatalog(null);
            setDraftMessage(`${scopeLabel(editingCatalog.scope)} draft saved. Publish ${scopeLabel(editingCatalog.scope)} Draft before Bundle Builder sees these values.`);
            await loadConfig({ showLoading: false });
        } catch (err) {
            setDraftError(err.message || `Failed to save ${scopeLabel(editingCatalog.scope)} draft.`);
        } finally {
            setSavingCatalogDraft(false);
        }
    };

    const saveTvDisplayDraft = async () => {
        if (!editingTvDisplay) {
            return;
        }

        setSavingTvDisplayDraft(true);
        setDraftError('');
        setDraftMessage('');

        try {
            const localErrors = validateTvDisplayEditor(editingTvDisplay.values);
            if (localErrors.length > 0) {
                throw new Error(localErrors.slice(0, 3).join(' '));
            }

            const updatedConfig = buildUpdatedTvDisplayConfig();
            const { response, data } = await savePricingDraft(
                updatedConfig,
                'tvDisplaySettings',
                `TV display draft updated: ${editingTvDisplay.packageKey}`
            );

            if (!response.ok || data.saved === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `TV Display Settings draft save failed with status ${response.status}`);
            }

            setEditingTvDisplay(null);
            setDraftMessage('TV Display Settings draft saved. Publish TV Display Settings Draft before Bundle Builder sees these display values.');
            await loadConfig({ showLoading: false });
        } catch (err) {
            setDraftError(err.message || 'Failed to save TV Display Settings draft.');
        } finally {
            setSavingTvDisplayDraft(false);
        }
    };

    const saveEmailShellDraft = async () => {
        if (!editingEmailShell) {
            return;
        }

        setSavingEmailShellDraft(true);
        setDraftError('');
        setDraftMessage('');

        try {
            const localErrors = validateEmailShellEditor(
                editingEmailShell,
                emailShellImageFields,
                emailShellSocialFields
            );
            if (localErrors.length > 0) {
                throw new Error(localErrors.slice(0, 3).join(' '));
            }

            const updatedConfig = buildUpdatedEmailShellConfig();
            const { response, data } = await savePricingDraft(
                updatedConfig,
                'emailShell',
                'Email Shell draft updated.'
            );

            if (!response.ok || data.saved === false) {
                const validationErrors = data.validation?.errors?.join(' ') || data.error;
                throw new Error(validationErrors || `Email Shell draft save failed with status ${response.status}`);
            }

            setEditingEmailShell(null);
            setDraftMessage('Email Shell draft saved. Publish Email Shell Draft before Bundle Builder preview sees these values.');
            await loadConfig({ showLoading: false });
        } catch (err) {
            setDraftError(err.message || 'Failed to save Email Shell draft.');
        } finally {
            setSavingEmailShellDraft(false);
        }
    };

    const scopeLabel = pricingScopeLabel;

    const deleteDraftOnlyProduct = async ({ scope, productId, groupName = null }) => {
        const activeProduct = scope === 'internet'
            ? findActiveInternetProduct(productId)
            : scope === 'homePhone'
                ? findActiveHomePhoneProduct(groupName, productId)
                : findActiveCatalogProduct(scope, productId, groupName);

        const draftProduct = scope === 'internet'
            ? getInternetProducts(draftConfig || config).find((product) => product.id === productId)
            : scope === 'homePhone'
                ? getHomePhoneGroup(draftConfig || config, groupName).find((product) => product.id === productId)
                : getCatalogProducts(draftConfig || config, scope, groupName).find((product) => product.id === productId);

        if (!isDraftOnlyProduct(draftProduct, activeProduct)) {
            setDraftError('Published products cannot be deleted. Archive removes the product from Bundle Builder while keeping history and rollback safe.');
            return;
        }

        if (!window.confirm(`Delete draft-only product "${draftProduct.displayName || productId}"? This cannot delete published products.`)) {
            return;
        }

        setDraftError('');
        setDraftMessage('');

        try {
            const nextConfig = JSON.parse(JSON.stringify(draftConfig || config));

            if (scope === 'internet') {
                nextConfig.products.internet = (nextConfig.products.internet || []).filter((product) => product.id !== productId);
            } else if (scope === 'homePhone') {
                nextConfig.products.homePhone[groupName] = (nextConfig.products.homePhone[groupName] || []).filter((product) => product.id !== productId);
            } else if (scope === 'mobility') {
                nextConfig.products.mobility.products = (nextConfig.products.mobility.products || []).filter((product) => product.id !== productId);
            } else if (scope === 'security') {
                nextConfig.products.security.products = (nextConfig.products.security.products || []).filter((product) => product.id !== productId);
            } else if (scope === 'addons') {
                const group = (nextConfig.products.addons.groups || []).find((candidate) => candidate.id === groupName);
                if (group) {
                    group.items = (group.items || []).filter((product) => product.id !== productId);
                }
            }

            await saveScopedDraftConfig(nextConfig, scope, `${scopeLabel(scope)} draft-only product deleted: ${productId}`);
            setEditingInternet(null);
            setEditingHomePhone(null);
            setEditingCatalog(null);
            setDraftMessage(`${scopeLabel(scope)} draft-only product deleted. Publish ${scopeLabel(scope)} Draft to finalize the draft state.`);
        } catch (err) {
            setDraftError(err.message || 'Failed to delete draft-only product.');
        }
    };

    const publishHomePhoneDraft = publishDraft;
    const rollbackHomePhoneConfig = rollbackConfig;

    const renderStatusBanners = () => (
        <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
            Product Pricing Manager controls non-TV Bundle Builder config and TV display metadata through scoped drafts, publish, and rollback. TV prices and recommendations remain controlled by TV V2.
        </Alert>
    );

    const renderSectionIntro = (title, body, chips = []) => (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack spacing={1}>
                <Typography variant="h6">{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {body}
                </Typography>
                {chips.length > 0 && (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {chips.map((chip) => (
                            <Chip key={chip} label={chip} size="small" variant="outlined" />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );

    const renderDraftMessages = () => (
        <>
            {draftMessage && <Alert severity="success" onClose={() => setDraftMessage('')}>{draftMessage}</Alert>}
            {draftError && <Alert severity="error" onClose={() => setDraftError('')}>{draftError}</Alert>}
        </>
    );

    const renderCategoryDraftStatus = (scope) => {
        if (!hasDraftChangesForScope(scope)) {
            return null;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ py: 1 }}>
                <Typography variant="body2" fontWeight={700}>{scopeLabel(scope)} draft differs from active.</Typography>
                <Typography variant="caption" color="text.secondary">
                    Publish this category when reviewed. Rollback controls live in Version History.
                </Typography>
            </Alert>
        );
    };

    const renderFutureControls = () => (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Coming Later</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Import/export, compare, and all-category publish helpers remain locked for later phases.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {['Compare All Categories', 'Import Config JSON', 'Export Config JSON', 'Publish Non-Home-Phone Categories'].map((label) => (
                        <Chip key={label} label={`${label} - later`} size="small" variant="outlined" />
                    ))}
                </Stack>
            </Stack>
        </Paper>
    );

    const renderStorageStatus = () => {
        const active = storageStatus?.active || config?.meta?.storage || null;
        const draft = draftStatus || storageStatus?.draft || null;

        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                    <Box>
                        <Typography variant="h6">Config Storage Foundation</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Persistent active/draft/version storage is live. Product categories can now be validated, published, and rolled back by scope.
                        </Typography>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Active version</Typography>
                            <Typography variant="body2" fontWeight={700}>{active?.versionNumber ?? 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Draft exists</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatBoolean(Boolean(draft))}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Active published</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatDateTime(active?.publishedAt)}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">Snapshots</Typography>
                            <Typography variant="body2" fontWeight={700}>{versions.length}</Typography>
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip label="Active config stored" size="small" color={active ? 'success' : 'default'} variant="outlined" />
                        <Chip label={draft ? 'Draft copy exists' : 'Draft copy missing'} size="small" color={draft ? 'success' : 'warning'} variant="outlined" />
                        <Chip label="Internet draft/publish enabled" size="small" color="primary" variant="outlined" />
                        <Chip label="Home Phone draft/publish enabled" size="small" color="success" variant="outlined" />
                        <Chip label="Mobility/Security/Add-ons enabled" size="small" color="info" variant="outlined" />
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    const renderPublishDialog = () => (
        <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Publish {scopeLabel(publishScope)} Draft</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning" variant="outlined">
                        Publishing makes {scopeLabel(publishScope)} draft values active. Bundle Builder will read active {scopeLabel(publishScope)} config after this succeeds.
                    </Alert>
                    <TextField
                        label="Change summary"
                        value={publishSummary}
                        onChange={(event) => setPublishSummary(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        required
                        helperText="Required for audit history."
                    />
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="body2" fontWeight={700}>Scope</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {scopeLabel(publishScope)} only. Other categories, TV V2, templates, and Send Offer are not changed by this action.
                        </Typography>
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setPublishDialogOpen(false)} disabled={publishing}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={publishHomePhoneDraft}
                    disabled={publishing || !publishSummary.trim()}
                >
                    {publishing ? 'Publishing...' : `Publish ${scopeLabel(publishScope)} Draft`}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderRollbackDialog = () => (
        <Dialog open={Boolean(rollbackTarget)} onClose={() => setRollbackTarget(null)} fullWidth maxWidth="sm">
            <DialogTitle>Rollback {scopeLabel(rollbackScope)} Config</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Alert severity="warning" variant="outlined">
                        Rollback creates a new active version from the selected archived version. It does not delete history.
                    </Alert>
                    <Typography variant="body2">
                        Target version: <strong>{rollbackTarget?.versionNumber || 'N/A'}</strong>
                    </Typography>
                    <TextField
                        label="Rollback summary"
                        value={rollbackSummary}
                        onChange={(event) => setRollbackSummary(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setRollbackTarget(null)} disabled={rollingBack}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={rollbackHomePhoneConfig}
                    disabled={rollingBack || !rollbackTarget}
                >
                    {rollingBack ? 'Rolling back...' : `Rollback ${scopeLabel(rollbackScope)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <PricingIcon color="primary" />
                        <Typography variant="h4">Product Pricing Manager</Typography>
                        <Chip label="Scoped Active Config Phase" color="primary" size="small" />
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                        Admin-only review surface for the future Bundle Builder config shape.
                    </Typography>
                </Box>
            </Box>

            {renderStatusBanners()}

            {loading && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" mt={2}>
                        Loading active and draft config...
                    </Typography>
                </Paper>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!loading && config && (
                <Stack spacing={3}>
                    <Paper sx={{ p: 1 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(event, value) => setActiveTab(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {categoryTabs.map((tab) => (
                                <Tab key={tab.id} value={tab.id} icon={tab.icon} iconPosition="start" label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip icon={<StatusIcon />} label="Admin only" color="success" variant="outlined" />
                        <Chip label="Non-TV + TV display + email shell publish enabled" color="primary" variant="outlined" />
                        <Chip label="Scoped draft safety" variant="outlined" />
                        <Chip label="TV V2 remains source of truth" color="primary" variant="outlined" />
                    </Stack>

                    {draftIntegrityWarnings.length > 0 && (
                        <Alert severity="warning" variant="outlined">
                            <Typography variant="body2" fontWeight={700}>
                                Draft config needs attention, but this page can still load.
                            </Typography>
                            {(showAllDraftWarnings ? draftIntegrityWarnings : draftIntegrityWarnings.slice(0, 3)).map((warning) => (
                                <Typography key={warning} variant="body2">
                                    {warning}
                                </Typography>
                            ))}
                            {draftIntegrityWarnings.length > 3 && (
                                <Button size="small" onClick={() => setShowAllDraftWarnings((current) => !current)} sx={{ mt: 0.5, px: 0 }}>
                                    {showAllDraftWarnings ? 'Show fewer warnings' : `Show all warnings (${draftIntegrityWarnings.length})`}
                                </Button>
                            )}
                        </Alert>
                    )}

                    <ProductPricingActiveTab
                        context={{
                            activeEmailShell,
                            activeTab,
                            chipColor,
                            config,
                            counts,
                            draftConfig,
                            draftEmailShell,
                            draftValidation,
                            emailShellDraftChanged,
                            emailShellImageFields,
                            findActiveCatalogProduct,
                            findActiveHomePhoneProduct,
                            findActiveInternetProduct,
                            findActiveTvDisplayPackage,
                            formatBoolean,
                            formatDateTime,
                            formatMoney,
                            futureControlCards,
                            getCatalogChangedFields,
                            getHomePhoneChangedFields,
                            getHomePhoneDisplayMetadata,
                            getHomePhoneGroup,
                            getInternetChangedFields,
                            getInternetDisplayMetadata,
                            getInternetProducts,
                            getMobilityAdminGroup,
                            getTvDisplayChangedFields,
                            getTvDisplayPackages,
                            hasDraftChangesForScope,
                            isCatalogDraftChanged,
                            isHomePhoneDraftChanged,
                            isInternetDraftChanged,
                            isTvDisplayDraftChanged,
                            mobilityAdminFilter,
                            mobilityAdminFilters,
                            normalizeArray,
                            openCatalogEditor,
                            openEmailShellEditor,
                            openHomePhoneEditor,
                            openInternetEditor,
                            openNewCatalogEditor,
                            openNewHomePhoneEditor,
                            openNewInternetEditor,
                            openPublishDialog,
                            openRollbackDialog,
                            openTvDisplayEditor,
                            renderCategoryDraftStatus,
                            renderDraftMessages,
                            renderFutureControls,
                            renderSectionIntro,
                            renderStorageStatus,
                            scopeLabel,
                            setEmailShellPreviewOpen,
                            setMobilityAdminFilter,
                            setShowAllValidationWarnings,
                            setShowMoreVersions,
                            setVersionHistoryScope,
                            shortText,
                            showAllValidationWarnings,
                            showMoreVersions,
                            sortedVersions,
                            sortProductsBySortOrder,
                            tvToolLinks,
                            tvV2PriceByKey,
                            tvV2Pricing,
                            validateDraftConfig,
                            validatingDraft,
                            versionHistoryScope
                        }}
                    />
                </Stack>
            )}
            <ProductPricingEditorDialogs
                context={{
                    addCatalogDisplayChip,
                    addCatalogLinePricingTier,
                    addCatalogTopBadge,
                    addDisplayChip,
                    addInternetDisplayChip,
                    addInternetTopBadge,
                    addTvDisplayChip,
                    addTvTopBadge,
                    chipColor,
                    config,
                    deleteDraftOnlyProduct,
                    draftConfig,
                    draftEmailShell,
                    editingCatalog,
                    editingEmailShell,
                    editingHomePhone,
                    editingInternet,
                    editingTvDisplay,
                    emailShellImageFields,
                    emailShellPreviewOpen,
                    emailShellSocialFields,
                    emailShellTextFields,
                    formatMoney,
                    findActiveCatalogProduct,
                    findActiveHomePhoneProduct,
                    findActiveInternetProduct,
                    isDraftOnlyProduct,
                    isValidSortOrderInput,
                    isHttpUrl,
                    linesFromText,
                    normalizeArray,
                    removeCatalogDisplayChip,
                    removeCatalogLinePricingTier,
                    removeCatalogTopBadge,
                    removeDisplayChip,
                    removeInternetDisplayChip,
                    removeInternetTopBadge,
                    removeTvDisplayChip,
                    removeTvTopBadge,
                    saveCatalogDraft,
                    saveEmailShellDraft,
                    saveHomePhoneDraft,
                    saveInternetDraft,
                    saveTvDisplayDraft,
                    savingCatalogDraft,
                    savingDraft,
                    savingEmailShellDraft,
                    savingInternetDraft,
                    savingTvDisplayDraft,
                    scopeLabel,
                    setEditingCatalog,
                    setEditingEmailShell,
                    setEditingHomePhone,
                    setEditingInternet,
                    setEditingTvDisplay,
                    setEmailShellPreviewOpen,
                    tvPvrModes,
                    tvV2PriceByKey,
                    updateCatalogDisplayChip,
                    updateCatalogEditorValue,
                    updateCatalogLinePricing,
                    updateCatalogLinePricingTier,
                    updateCatalogTopBadge,
                    updateDisplayChip,
                    updateEditorValue,
                    updateEmailShellSocialValue,
                    updateEmailShellValue,
                    updateInternetDisplayChip,
                    updateInternetEditorValue,
                    updateInternetTopBadge,
                    updateInternetTvPriceAdjustmentValue,
                    updateHomePhoneTopBadge,
                    addHomePhoneTopBadge,
                    removeHomePhoneTopBadge,
                    updateTvDisplayChip,
                    updateTvDisplayEditorValue,
                    updateTvTopBadge,
                    validateAddonsEditor,
                    validateEmailShellEditor,
                    validateHomePhoneEditor,
                    validateInternetEditor,
                    validateMobilityEditor,
                    validateSecurityEditor,
                    validateTvDisplayEditor
                }}
            />
            {renderPublishDialog()}
            {renderRollbackDialog()}
        </Box>
    );
}

export default ProductPricingManagerWorkspace;
